import { useEffect, useRef, useState } from 'react'

const MAX_STORED_MESSAGES = 80

function pendingSuggestions(suggestions = []) {
  return suggestions.map((suggestion) => ({
    ...suggestion,
    status: 'pending',
  }))
}

function restoredMessages(messages) {
  const restored = messages.map((message) => ({
    ...message,
    fieldSuggestions: (message.fieldSuggestions || []).map((suggestion) => ({
      ...suggestion,
      status: suggestion.status === 'applied' ? 'pending' : suggestion.status,
    })),
  }))

  return restored.filter((message, index) => {
    const previousMessage = restored[index - 1]
    return message.role !== 'user'
      || previousMessage?.role !== 'user'
      || previousMessage.content !== message.content
  })
}

function conversationBeforeRetry(messages, message) {
  const firstRetryIndex = messages.findLastIndex((candidate) => (
    candidate.role !== 'user' || candidate.content !== message
  )) + 1

  return firstRetryIndex === messages.length ? messages : messages.slice(0, firstRetryIndex)
}

function storedMessages(storageKey, initialMessages) {
  if (!storageKey || typeof window === 'undefined') return restoredMessages(initialMessages)

  try {
    const storedValue = window.localStorage.getItem(storageKey)
      ?? window.sessionStorage.getItem(storageKey)
    const parsedMessages = JSON.parse(storedValue)
    return Array.isArray(parsedMessages) ? restoredMessages(parsedMessages) : restoredMessages(initialMessages)
  } catch {
    return restoredMessages(initialMessages)
  }
}

function removeStoredMessages(storageKey) {
  if (!storageKey || typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(storageKey)
    window.sessionStorage.removeItem(storageKey)
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function persistStoredMessages(storageKey, messages) {
  if (!storageKey || typeof window === 'undefined') return

  try {
    if (messages.length === 0) {
      window.localStorage.removeItem(storageKey)
      window.sessionStorage.removeItem(storageKey)
      return
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)),
    )
    window.sessionStorage.removeItem(storageKey)
  } catch {
    // Keep chat usable when browser storage is unavailable or full.
  }
}

export function useAskRobChat({ initialMessages = [], storageKey = null } = {}) {
  const [messages, setMessages] = useState(() => storedMessages(storageKey, initialMessages))
  const [error, setError] = useState(null)
  const [activeRequestGeneration, setActiveRequestGeneration] = useState(null)
  const [failedMessage, setFailedMessage] = useState(null)
  const failedRequestRef = useRef(null)
  const requestGenerationRef = useRef(0)
  const initialMessagesSignature = JSON.stringify(initialMessages)
  const [restoredInitialMessagesSignature, setRestoredInitialMessagesSignature] = useState(
    initialMessagesSignature,
  )
  const isLoading = activeRequestGeneration !== null

  if (restoredInitialMessagesSignature !== initialMessagesSignature) {
    setRestoredInitialMessagesSignature(initialMessagesSignature)
    if (messages.length === 0 && initialMessages.length > 0) {
      setMessages(restoredMessages(initialMessages))
    }
  }

  useEffect(() => {
    persistStoredMessages(storageKey, messages)
  }, [messages, storageKey])

  const updateSuggestionStatus = (messageIndex, suggestionId, status) => {
    setMessages((currentMessages) => currentMessages.map((message, index) => {
      if (index !== messageIndex) return message

      return {
        ...message,
        fieldSuggestions: (message.fieldSuggestions || []).map((suggestion) => (
          suggestion.id === suggestionId ? { ...suggestion, status } : suggestion
        )),
      }
    }))
  }

  const dismissSuggestion = (messageIndex, suggestionId) => {
    updateSuggestionStatus(messageIndex, suggestionId, 'dismissed')
  }

  const findSuggestion = (messageIndex, suggestionId) => (
    messages[messageIndex]?.fieldSuggestions?.find((suggestion) => suggestion.id === suggestionId)
  )

  const submitMessage = async (message, request) => {
    const requestGeneration = requestGenerationRef.current + 1
    requestGenerationRef.current = requestGeneration
    const priorMessages = conversationBeforeRetry(messages, message)
    const nextMessages = [...priorMessages, { role: 'user', content: message }]
    persistStoredMessages(storageKey, nextMessages)
    setMessages(nextMessages)
    setError(null)
    setFailedMessage(null)
    failedRequestRef.current = null
    setActiveRequestGeneration(requestGeneration)

    try {
      const response = await request({ message, conversation: priorMessages })
      if (requestGeneration !== requestGenerationRef.current) return null

      const completedMessages = [
        ...nextMessages,
        {
          role: 'assistant',
          content: response.assistantMessage,
          fieldSuggestions: pendingSuggestions(response.fieldSuggestions),
          lifestyleSummary: response.lifestyleSummary,
        },
      ]
      persistStoredMessages(storageKey, completedMessages)
      setMessages(completedMessages)
      return response
    } catch (requestError) {
      if (requestGeneration !== requestGenerationRef.current) return null

      setError(requestError.message)
      setFailedMessage(message)
      failedRequestRef.current = request
      setMessages(nextMessages)
      return null
    } finally {
      setActiveRequestGeneration((currentGeneration) => (
        currentGeneration === requestGeneration ? null : currentGeneration
      ))
    }
  }

  const retryLastMessage = () => {
    if (!failedMessage || !failedRequestRef.current || isLoading) return Promise.resolve(null)

    return submitMessage(failedMessage, failedRequestRef.current)
  }

  const clearMessages = () => {
    requestGenerationRef.current += 1
    removeStoredMessages(storageKey)
    setMessages([])
    setError(null)
    setFailedMessage(null)
    setActiveRequestGeneration(null)
    failedRequestRef.current = null
  }

  return {
    messages,
    error,
    failedMessage,
    isLoading,
    canRetry: Boolean(failedMessage),
    submitMessage,
    retryLastMessage,
    findSuggestion,
    updateSuggestionStatus,
    dismissSuggestion,
    clearMessages,
  }
}
